if (!customElements.get('wlsn-buybox')) {
  class WLSNBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        buyboxRoot: 'wlsn-buybox',
        productVariants: 'wlsn-product-variants',
        productForm: 'wlsn-product-form',
      };

      this.replaceScope = {
        ALL: 'all',
        PRODUCT_FORM: 'productForm',
      };

      this.bindMethods = {
        onPopState: this._onPopState.bind(this),
        onPageUpdated: this._onPageUpdated.bind(this),
        onVariantChange: this._onVariantChange.bind(this),
      };

      this.isProductPage =
        this.getAttribute('data-is-product-page') === 'true' && window.location.pathname.startsWith('/products/');

      this.templateName = this.getAttribute('data-template-name') || '';

      this.isUpdating = false;
    }

    /**
     * Get elements
     * @returns {Object} - Object containing DOM element references
     */
    get elements() {
      return {
        productVariants: this.querySelectorAll(this.classes.productVariants),
      };
    }

    /**
     * @param {boolean|{updateHistory?: boolean, replaceScope?: string}} options
     * @returns {{ updateHistory: boolean, replaceScope: string }}
     */
    _normalizePageUpdateOptions(options) {
      if (typeof options === 'boolean') {
        return { updateHistory: options, replaceScope: this.replaceScope.ALL };
      }
      const o = options && typeof options === 'object' ? options : {};
      return {
        updateHistory: o.updateHistory !== false,
        replaceScope: o.replaceScope === this.replaceScope.PRODUCT_FORM ? this.replaceScope.PRODUCT_FORM : this.replaceScope.ALL,
      };
    }

    /**
     * @param {{ replaceScope?: string }} [options]
     * @returns {{ replaceScope: string }}
     */
    _normalizeContentUpdateOptions(options) {
      const o = options && typeof options === 'object' ? options : {};
      return {
        replaceScope: o.replaceScope === this.replaceScope.PRODUCT_FORM ? this.replaceScope.PRODUCT_FORM : this.replaceScope.ALL,
      };
    }

    /**
     * @returns {string|null}
     */
    _getBuyboxSectionDomId() {
      return (
        this.getAttribute('data-section-id') || this.closest('[id^="shopify-section-"]')?.id || null
      );
    }

    /**
     * @param {Document|DocumentFragment|Element} doc
     * @returns {Element|null}
     */
    _getSectionRootInDocument(doc, sectionId) {
      if (!sectionId || !doc) return null;
      return doc.querySelector(`#${CSS.escape(sectionId)}`);
    }

    /**
     * Section root for this buybox inside fetched HTML. Exact id first; then same section key as
     * `_replaceAllSectionsFromViewDocument` (LP section instance id differs from product ?view= response).
     * @param {Document} doc
     * @returns {Element|null}
     */
    _findBuyboxSectionInFetchedDocument(doc) {
      const sectionId = this._getBuyboxSectionDomId();
      if (sectionId) {
        const byId = this._getSectionRootInDocument(doc, sectionId);
        if (byId) return byId;
      }

      const keySourceId = sectionId || this.closest('[id^="shopify-section-"]')?.id;
      const targetKey = keySourceId ? this._getSectionKeyFromId(keySourceId) : null;
      if (!targetKey || !doc) return null;

      const candidates = doc.querySelectorAll('[id^="shopify-section-"]');
      for (let i = 0; i < candidates.length; i++) {
        const el = candidates[i];
        if (this._getSectionKeyFromId(el.id) !== targetKey) continue;
        if (el.querySelector(this.classes.buyboxRoot)) return el;
      }
      return null;
    }

    /**
     * Run inline script that sets window.productVariants (section renders it after buybox markup).
     * @param {Element|null} sectionRoot
     * @returns {void}
     */
    _syncProductVariantsScript(sectionRoot) {
      if (!sectionRoot) return;
      const scripts = sectionRoot.querySelectorAll('script:not([src])');
      for (const oldScript of scripts) {
        const text = oldScript.textContent || '';
        if (!/window\.productVariants\s*=/.test(text)) continue;
        const newScript = document.createElement('script');
        newScript.textContent = oldScript.textContent;
        document.documentElement.appendChild(newScript);
        newScript.remove();
        break;
      }
    }

    /**
     * Replace only wlsn-product-form inside this buybox from a fetched section fragment.
     * @param {Element|null} newSectionRoot - Section node from parsed response
     * @returns {boolean}
     */
    _replaceProductFormFromFetchedSection(newSectionRoot) {
      if (!newSectionRoot) return false;

      const currentForm = this.querySelector(this.classes.productForm);
      const newForm = newSectionRoot.querySelector(this.classes.productForm);

      if (!currentForm || !newForm) return false;

      const importedForm = document.importNode(newForm, true);
      currentForm.replaceWith(importedForm);
      this._executeScripts(importedForm);
      this._syncProductVariantsScript(newSectionRoot);
      return true;
    }

    /**
     * Connected callback method
     * @returns {void}
     */
    connectedCallback() {
      this._attachEventListeners();
      // Handle browser back/forward navigation
      window.addEventListener('popstate', this.bindMethods.onPopState);
      // Listen for page updates to reattach listeners
      window.addEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Disconnected callback method
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachEventListeners();
      window.removeEventListener('popstate', this.bindMethods.onPopState);
      window.removeEventListener('wlsn:page:updated', this.bindMethods.onPageUpdated);
    }

    /**
     * Attach event listeners
     * @returns {void}
     */
    _attachEventListeners() {
      const { productVariants } = this.elements;

      productVariants.forEach((el) => {
        el.addEventListener('wlsn:buybox:product-variant-change', this.bindMethods.onVariantChange);
      });
    }

    /**
     * Detach event listeners
     * @returns {void}
     */
    _detachEventListeners() {
      const { productVariants } = this.elements;

      productVariants.forEach((el) => {
        el.removeEventListener('wlsn:buybox:product-variant-change', this.bindMethods.onVariantChange);
      });
    }

    /**
     * Handle page updated event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPageUpdated(event) {
      // Re-attach event listeners after page update
      this._detachEventListeners();
      this._attachEventListeners();
    }

    /**
     * Handle variant change event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onVariantChange(event) {
      if (event.detail && event.detail.url) {
        const opts = { replaceScope: this.replaceScope.PRODUCT_FORM, updateHistory: true };
        this.isProductPage
          ? this._updateProductPage(event.detail.url, opts)
          : this._updateProductContent(event.detail.url, opts);
      }
    }

    /**
     * Handle pop state event
     * @param {Event} event - Event object
     * @returns {void}
     */
    _onPopState(event) {
      // Handle browser back/forward navigation
      this._updateProductPage(window.location.href, { updateHistory: false, replaceScope: this.replaceScope.ALL });
    }

    /**
     * Update product page (PDP fetch + patch)
     * @param {string} url - URL to update
     * @param {boolean|{updateHistory?: boolean, replaceScope?: string}} [options]
     * @returns {void}
     */
    async _updateProductPage(url, options) {
      if (this.isUpdating) return;

      const { updateHistory, replaceScope } = this._normalizePageUpdateOptions(options);

      try {
        this.isUpdating = true;

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        let patched = false;
        if (replaceScope === this.replaceScope.PRODUCT_FORM) {
          patched = this._tryPatchProductFormPDP(newDocument);
        }

        if (!patched) {
          this._replaceAllSectionsInMainFromDocument(newDocument, url, updateHistory, this.replaceScope.ALL);
          return;
        }

        if (updateHistory) {
          window.history.pushState({ path: url }, '', url);
        }

        window.dispatchEvent(
          new CustomEvent('wlsn:page:updated', {
            detail: { url, replaceScope: this.replaceScope.PRODUCT_FORM },
          })
        );
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * @param {Document} newDocument
     * @returns {boolean}
     */
    _tryPatchProductFormPDP(newDocument) {
      const newSection = this._findBuyboxSectionInFetchedDocument(newDocument);
      return this._replaceProductFormFromFetchedSection(newSection);
    }

    /**
     * @param {Document} newDocument
     * @param {string} url
     * @param {boolean} updateHistory
     * @param {string} scopeForEvent
     * @returns {void}
     */
    _replaceAllSectionsInMainFromDocument(newDocument, url, updateHistory, scopeForEvent) {
      const currentMain = document.querySelector('main');
      const newMain = newDocument.querySelector('main');

      if (!currentMain || !newMain) {
        throw new Error('Could not find main content container');
      }

      const currentSections = currentMain.querySelectorAll('[id^="shopify-section-"]');
      let replacedCount = 0;

      currentSections.forEach((currentSection) => {
        const sectionId = currentSection.id;
        if (!sectionId) return;

        const newSection = newMain.querySelector(`#${CSS.escape(sectionId)}`);

        if (newSection) {
          currentSection.innerHTML = newSection.innerHTML;
          this._executeScripts(currentSection);
          replacedCount++;
        }
      });

      if (replacedCount === 0) {
        window.location.href = url;
        return;
      }

      if (updateHistory) {
        window.history.pushState({ path: url }, '', url);
      }

      window.dispatchEvent(
        new CustomEvent('wlsn:page:updated', {
          detail: { url, replaceScope: scopeForEvent },
        })
      );
    }

    /**
     * Update product content (alternate template / view)
     * @param {string} url - URL to update
     * @param {{ replaceScope?: string }} [options]
     * @returns {void}
     */
    async _updateProductContent(url, options) {
      if (this.isUpdating) return;

      if (!this.templateName || this.templateName === 'none') {
        return;
      }

      const { replaceScope } = this._normalizeContentUpdateOptions(options);

      try {
        this.isUpdating = true;

        const fetchUrl = new URL(url, window.location.href);
        fetchUrl.searchParams.set('view', this.templateName);
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error('Failed to fetch product page');

        const html = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(html, 'text/html');

        let patched = false;
        if (replaceScope === this.replaceScope.PRODUCT_FORM) {
          patched = this._tryPatchProductFormFromView(newDocument);
        }

        if (!patched) {
          this._replaceAllSectionsFromViewDocument(newDocument, fetchUrl.toString(), this.replaceScope.ALL);
          return;
        }

        window.dispatchEvent(
          new CustomEvent('wlsn:page:updated', {
            detail: { url: fetchUrl.toString(), replaceScope: this.replaceScope.PRODUCT_FORM },
          })
        );
      } catch (error) {
        console.error('Error updating product page:', error);
        window.location.href = url;
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * @param {Document} newDocument
     * @returns {boolean}
     */
    _tryPatchProductFormFromView(newDocument) {
      const newSection = this._findBuyboxSectionInFetchedDocument(newDocument);
      return this._replaceProductFormFromFetchedSection(newSection);
    }

    /**
     * @param {Document} newDocument
     * @param {string} detailUrl
     * @param {string} scopeForEvent
     * @returns {void}
     */
    _replaceAllSectionsFromViewDocument(newDocument, detailUrl, scopeForEvent) {
      const currentSections = document.querySelectorAll('[id^="shopify-section-"]');
      const newSections = newDocument.querySelectorAll('[id^="shopify-section-"]');

      const currentByKey = new Map();
      currentSections.forEach((section) => {
        const key = this._getSectionKeyFromId(section.id);
        if (!key) return;
        currentByKey.set(key, section);
      });

      newSections.forEach((newSection) => {
        const key = this._getSectionKeyFromId(newSection.id);
        if (!key) return;

        const currentSection = currentByKey.get(key);
        if (!currentSection) return;

        currentSection.replaceWith(newSection);
        this._executeScripts(newSection);
      });

      window.dispatchEvent(
        new CustomEvent('wlsn:page:updated', {
          detail: { url: detailUrl, replaceScope: scopeForEvent },
        })
      );
    }

    /**
     * Get section key from id
     * @param {string} sectionId - Section id
     * @returns {string} - Section key
     */
    _getSectionKeyFromId(sectionId) {
      const match = sectionId.match(/__(.+)_([A-Za-z0-9]+)$/);
      return match ? match[1] : null;
    }

    /**
     * Execute scripts in container element
     * @param {Element} container - Container element
     * @returns {void}
     */
    _executeScripts(container) {
      const scripts = container.querySelectorAll('script');

      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        newScript.textContent = oldScript.textContent;

        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }
  customElements.define('wlsn-buybox', WLSNBuybox);
}
